import { ColumnsType } from "antd/es/table";
import { ReactNode, useMemo } from "react";
import { IDocumentExtended } from "../../../../../../../types/Documents";
import dayjs from "../../../../../../../configs/dayjs";
import { DATE_FORMAT } from "../../../../../../../lib/constants";
import { FileTextOutlined } from "@ant-design/icons";
import { PortalVisibilityLabel } from "../components/PortalVisabilityLabel";
import { TableActions } from "../components/TableActions";
import { useButtonHandlers } from "./useButtonHandlers";
import DOCUMENT_VISIBILITY from "../../../../../../../types/enums/DocumentVisibility";

type TTableColumnsProps = {
  data?: IDocumentExtended[];
  reloadData: () => void;
  setId: React.Dispatch<React.SetStateAction<string | null>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openDocument: (id: string) => void;
};

export const useGetTableColumns = ({
  data,
  reloadData,
  setId,
  setIsOpen,
  openDocument,
}: TTableColumnsProps) => {
  const { onDeleteBtnClick } = useButtonHandlers({
    reloadData,
    setIsOpen,
    setId,
  });

  const columnWidths = useMemo(() => {
    const hasTripleVisibility =
      data?.some(({ visibility }) => visibility?.length === 3) ?? false;

    if (hasTripleVisibility) {
      return {
        name: "22%",
        description: "22%",
        visibility: "16%",
      };
    }

    const hasDoubleVisibility =
      data?.some(({ visibility }) => visibility?.length === 2) ?? false;

    if (hasDoubleVisibility) {
      return {
        name: "23%",
        description: "23%",
        visibility: "14%",
      };
    }

    return {
      name: "24%",
      description: "24%",
      visibility: "12%",
    };
  }, [data]);

  const columns: ColumnsType<IDocumentExtended> = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: "8%",
        sorter: true,
        render: (value: string): string => dayjs(value).format(DATE_FORMAT),
      },
      {
        title: "Document#",
        dataIndex: "number",
        key: "number",
        width: "10%",
        sorter: true,
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: columnWidths.name,
        sorter: true,
        ellipsis: true,
        render: (value: string, record: IDocumentExtended): ReactNode => {
          const className = `mr-2 ${record.uploaded ? "ant-btn-link" : "text-md title"}`;

          return (
            <div className="elipsis">
              <FileTextOutlined className={className} />
              <span>{value}</span>
            </div>
          );
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
        width: columnWidths.description,
      },
      {
        title: "Holding Original",
        dataIndex: "isHolding",
        key: "isHolding",
        width: "12%",
        sorter: true,
        render: (value: boolean): string => (value ? "Yes" : "No"),
      },
      {
        title: "Portal Visibility",
        dataIndex: "visibility",
        key: "visibility",
        width: columnWidths.visibility,
        sorter: true,
        render: (val: DOCUMENT_VISIBILITY[]): ReactNode => (
          <PortalVisibilityLabel val={val} tooltipLabel="document" />
        ),
      },
      {
        key: "actions",
        width: "10%",
        render: (_: unknown, record: IDocumentExtended): ReactNode => {
          const { id, uploaded, fileName } = record;

          return (
            <TableActions
              id={id}
              uploaded={uploaded}
              fileName={fileName}
              openDocument={openDocument}
              onDeleteBtnClick={onDeleteBtnClick}
            />
          );
        },
      },
    ],
    [columnWidths, openDocument, onDeleteBtnClick],
  );

  return { columns };
};
